import { useEffect } from 'react';
import { setHostBottomPanelOpen } from '../utils/iosWebToolBridge';

/**
 * Stow the iOS host's bottom-left chrome — the Command Center logo button and
 * any tutorial coach-mark anchored to it — for as long as this component is
 * showing a tool window. See `utils/iosWebToolBridge` for why the host has to be
 * told rather than dodged in CSS.
 *
 * Two subtleties, both learned from the flows this serves.
 *
 * **It is ref-counted**, because more than one caller can be open at once and a
 * naive per-component effect would let the inner one's cleanup unhide the button
 * while the outer one is still on screen.
 *
 * **The close is deferred by a microtask**, because the count legitimately
 * touches zero mid-handover. `RoomPickerModal` calls `onPick` (which opens the
 * generator) and then `onClose` in the same click handler, so React batches both
 * into one commit — and runs every cleanup before every setup. Posting `false`
 * synchronously there would flash the logo button and re-present the coach mark
 * for a frame, right on top of the generator that was opening. Deferring and
 * re-checking makes the handover invisible; a genuine close still posts on the
 * very next tick.
 */

// How many callers want the chrome stowed, and what the host has actually been
// told. Two different facts: during a handover the count drops to zero and back
// while the host's belief should never change, and re-posting `true` there
// would make the host redraw for nothing.
let openCount = 0;
// Of the open windows, how many are TOOLS and how many are mere CHOOSERS.
//
// Three kinds, not two, and that is the fix rather than a refinement. The rule
// used to be `chooserCount === openCount` — every open window is a chooser —
// which is only correct if every holder of this refcount is a tool window. Six
// are not: the minimap (one tap, and it stays expanded), the elements tray, the
// elevations preview strip and its minimap, and the dorm theme picker all stow
// the chrome because they would otherwise sit under it. With the minimap
// expanded, `openCount` is 2 and `chooserCount` 1, so a room picker opened by a
// lesson was reported to the host as the TOOL — and the lesson then narrated
// "tap any surface or fitting to swap it" over a list of rooms.
//
// So furniture does not vote. A window is a chooser when a chooser is open and
// no tool is: that still keeps the case the old rule was written for, where a
// picker layered UNDER the tool it opened must not downgrade the tool.
let toolCount = 0;
let chooserCount = 0;
let hostSuppressed = false;
let hostToldKind = null;
let pendingClose = null;

// What the OPEN windows amount to, as one word for the host.
//
// `chooser` and `widget` are both "not the tool", but they are not the same
// thing and the host needs them apart: a tutorial lesson asks "is the window I
// sent you into open?", and collapsing furniture into `chooser: false` made an
// expanded MINIMAP answer yes. The lesson then narrated its in-panel line over
// the plain viewer and asked for a ✕ pulse on a window that was not there.
function openKind() {
  if (toolCount > 0) return 'tool';
  if (chooserCount > 0) return 'chooser';
  return 'widget';
}

function syncHost() {
  const kind = openKind();
  if (hostSuppressed && hostToldKind === kind) return;
  hostSuppressed = true;
  hostToldKind = kind;
  // `chooser` stays on the wire beside `kind` because shipped hosts read it
  // and know nothing of `kind`; dropping it would change their behaviour.
  setHostBottomPanelOpen(true, kind === 'chooser', kind);
}

function acquire(kind) {
  openCount += 1;
  if (kind === 'tool') toolCount += 1;
  if (kind === 'chooser') chooserCount += 1;
  // A handover: the close we were about to post is no longer wanted.
  pendingClose = null;
  syncHost();
}

function release(kind) {
  openCount = Math.max(0, openCount - 1);
  if (kind === 'tool') toolCount = Math.max(0, toolCount - 1);
  if (kind === 'chooser') chooserCount = Math.max(0, chooserCount - 1);
  // Still something open — but possibly of a different KIND now, which is
  // exactly the picker-closes-and-leaves-the-tool case.
  if (openCount > 0) {
    syncHost();
    return;
  }
  const token = {};
  pendingClose = token;
  Promise.resolve().then(() => {
    if (pendingClose !== token || openCount > 0 || !hostSuppressed) return;
    pendingClose = null;
    hostSuppressed = false;
    hostToldKind = null;
    setHostBottomPanelOpen(false);
  });
}

/**
 * @param {boolean} active  true while this component's window is showing
 * @param {{kind?: 'tool'|'chooser'|'widget'}} [options]
 *   What is being shown, which decides whether it gets a say in what the host is
 *   told. `tool` is the window a tutorial narrates; `chooser` is a step on the
 *   way to one; `widget` is furniture that merely needs the chrome out of its
 *   way and must not be mistaken for either. Defaults to `widget`, so a caller
 *   that has not thought about it cannot mislabel someone else's tool.
 */
export function useHostChromeSuppressed(active, { kind = 'widget' } = {}) {
  useEffect(() => {
    if (!active) return undefined;
    acquire(kind);
    return () => release(kind);
  }, [active, kind]);
}

/** Test-only: drop the module-level state between cases. */
export function __resetHostChromeSuppression() {
  openCount = 0;
  toolCount = 0;
  chooserCount = 0;
  hostSuppressed = false;
  hostToldKind = null;
  pendingClose = null;
}

export default useHostChromeSuppressed;
